// /pages/FleetManagement/Helpers/components/HelperForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    VStack,
    HStack,
    Text,
    Grid,
    Heading,
    Select,
    FormHelperText,
} from '@chakra-ui/react';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { getPersonNameRegisterOptions, sanitizePersonNameInput } from '@/lib/utils/personName';

interface HelperFormInputs {
    first_name: string;
    last_name: string;
    contact_number: string;
    is_active: number;
}

interface HelperFormProps {
    helperId?: number;
    initialData?: any;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export function HelperForm({ helperId, initialData, onSuccess, onCancel }: HelperFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showError, showSuccess } = useToastNotification();

    const isEditMode = !!helperId;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<HelperFormInputs>({
        defaultValues: {
            first_name: '',
            last_name: '',
            contact_number: '',
            is_active: 1,
        },
    });

    useEffect(() => {
        if (initialData) {
            reset({
                first_name: initialData.first_name,
                last_name: initialData.last_name,
                contact_number: initialData.contact_number,
                is_active: initialData.is_active ?? 1,
            });
        }
    }, [initialData, reset]);

    const onSubmit: SubmitHandler<HelperFormInputs> = async (data) => {
        setIsSubmitting(true);
        try {
            let endpoint = isEditMode ? `/helpers/${helperId}` : '/helpers';
            if (isEditMode) {
                await apiClient.put(endpoint, data);
            } else {
                await apiClient.post(endpoint, data);
            }

            showSuccess('Success', `Helper ${data.first_name} ${isEditMode ? 'updated' : 'created'} successfully.`);
            onSuccess?.();
        } catch (error: any) {
            showError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="700px">
            <VStack spacing={6} align="stretch">
                {/* Heading matched to Truck Title size */}
                <Box>
                    <Heading size="md" mb={1}>
                        {isEditMode ? 'Edit Helper' : 'Add New Helper'}
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                        {isEditMode 
                            ? 'Update helper information.' 
                            : 'Register a new helper. You can assign them to drivers/trucks in the Drivers Management section.'}
                    </Text>
                </Box>

                <VStack spacing={4} align="stretch">
                    {/* First Name */}
                    {/* <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                        <FormLabel pt={0} mb={0} fontWeight="medium">
                            First Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <FormControl isInvalid={!!errors.first_name}>
                            <Input 
                                {...register('first_name', { required: 'First name is required' })} 
                                placeholder="Enter first name" 
                            />
                            <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
                        </FormControl>
                    </Grid> */}
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                        <FormLabel mb={0} fontWeight="medium">
                            First Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <FormControl isInvalid={!!errors.first_name}>
                            <Input 
                                {...register('first_name', getPersonNameRegisterOptions('First name is required'))}
                                placeholder="Enter first name"
                                onInput={(e) => {
                                    e.currentTarget.value = sanitizePersonNameInput(e.currentTarget.value);
                                }}
                            />
                            <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
                        </FormControl>
                    </Grid>

                    {/* Last Name */}
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                        <FormLabel mb={0} fontWeight="medium">
                            Last Name <Text as="span" color="red.500">*</Text>
                        </FormLabel>
                        <FormControl isInvalid={!!errors.last_name}>
                            <Input 
                                {...register('last_name', getPersonNameRegisterOptions('Last name is required'))}
                                placeholder="Enter last name"
                                onInput={(e) => {
                                    e.currentTarget.value = sanitizePersonNameInput(e.currentTarget.value);
                                }}
                            />
                            <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
                        </FormControl>
                    </Grid>

                    {/* Contact Number */}
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                        <FormLabel mb={0} fontWeight="medium">Contact No. <Text as="span" color="red.500">*</Text></FormLabel>
                        <FormControl isInvalid={!!errors.contact_number}>
                            <Input 
                                {...register('contact_number', {
                                    required: 'Contact number is required',
                                    validate: (value) => {
                                        // Pattern 1: Starts with 0 and followed by exactly 10 digits (Total 11)
                                        const startsWithZero = /^0\d{10}$/.test(value);
                                        // Pattern 2: Starts with +63 and followed by exactly 10 digits (Total 12 characters)
                                        const startsWithPH = /^\+63\d{10}$/.test(value);
                                        
                                        return startsWithZero || startsWithPH || 'Must be 11 digits (starting with 0) or 12 characters (starting with +63)';
                                    }
                                })} 
                                placeholder="0XXXXXXXXXX or +63XXXXXXXXXX"
                                onInput={(e) => {
                                    // Keep only numbers and the plus sign
                                    e.currentTarget.value = e.currentTarget.value.replace(/[^0-9+]/g, '');
                                }}
                            />
                            <FormErrorMessage>
                                {errors.contact_number?.message}
                            </FormErrorMessage>
                            <FormHelperText fontSize="xs">
                                Enter an 11-digit number starting with 0, or 12 characters starting with +63.
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* Status - Shown in Edit Mode */}
                    {isEditMode && (
                        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                            <FormLabel pt={2} fontWeight="medium">Status</FormLabel>
                            <FormControl isInvalid={!!errors.is_active}>
                                <Select {...register('is_active', { valueAsNumber: true })} bg="white">
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </Select>
                                <FormHelperText mt={2} lineHeight="short">
                                    Control whether this helper is available for assignment.
                                </FormHelperText>
                                <FormErrorMessage>{errors.is_active?.message}</FormErrorMessage>
                            </FormControl>
                        </Grid>
                    )}
                </VStack>

                {/* Action Buttons */}
                <HStack spacing={4} justify="flex-end" pt={4}>
                    {onCancel && (
                        <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button 
                        type="submit" 
                        colorScheme="blue" 
                        isLoading={isSubmitting}
                        px={8}
                    >
                        {isEditMode ? 'Save' : 'Create Helper'}
                    </Button>
                </HStack>
            </VStack>
        </Box>
    );
}
